import React from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Switch,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import TimelineIcon from '@mui/icons-material/Timeline';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import useThemeStyles from '../../../hooks/useThemeStyles';

const EXTRACTION_OPTIONS = [
  {
    key: 'characters',
    label: 'Characters',
    description: 'Detect named characters, their roles and traits',
    icon: PersonIcon,
  },
  {
    key: 'timeline',
    label: 'Timeline events',
    description: 'Extract dated events into your project timeline',
    icon: TimelineIcon,
  },
  {
    key: 'relationships',
    label: 'Family relationships',
    description: 'Detect family ties and build family trees',
    icon: FamilyRestroomIcon,
  },
];

/**
 * Options for the post-import AI analysis pass. The manuscript is split into
 * chunks in the browser and analyzed chunk by chunk.
 */
const AiExtractionStep = ({ aiOptions, onChange }) => {
  const { colors } = useThemeStyles();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2" sx={{ color: colors.text.secondary, textAlign: 'center' }}>
        After the manuscript is imported, PlotWyze can analyze it to build your project notes
        automatically.
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={aiOptions.enabled}
            onChange={() => onChange('enabled')}
            color="primary"
          />
        }
        label={
          <Typography sx={{ color: colors.text.primary, fontWeight: 500 }}>
            Run AI analysis after import
          </Typography>
        }
      />

      <FormGroup sx={{ pl: 1, opacity: aiOptions.enabled ? 1 : 0.5 }}>
        {EXTRACTION_OPTIONS.map((option) => {
          const Icon = option.icon;
          return (
            <FormControlLabel
              key={option.key}
              disabled={!aiOptions.enabled}
              control={
                <Checkbox
                  checked={Boolean(aiOptions[option.key])}
                  onChange={() => onChange(option.key)}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                  <Icon fontSize="small" sx={{ color: colors.interactive.primary }} />
                  <Box>
                    <Typography variant="body2" sx={{ color: colors.text.primary }}>
                      {option.label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: colors.text.secondary }}>
                      {option.description}
                    </Typography>
                  </Box>
                </Box>
              }
            />
          );
        })}
      </FormGroup>

      <Alert severity="info">
        Large manuscripts are split into smaller chunks and analyzed piece by piece, so this works
        for full-length novels. You can watch the progress during import.
      </Alert>
    </Box>
  );
};

AiExtractionStep.propTypes = {
  aiOptions: PropTypes.shape({
    enabled: PropTypes.bool,
    characters: PropTypes.bool,
    timeline: PropTypes.bool,
    relationships: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default AiExtractionStep;
